import React from 'react';
import { negate } from 'lodash/fp';
import { TreeView } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import makeStyles from '@mui/styles/makeStyles';
import {
  Classroom,
  Group,
  Student,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { isInGroup } from 'util/classroom-util';
import CheckBoxTreeItem from './CheckBoxTreeItem';

type ClassroomTreeViewProps = {
  classroom: Classroom;
  values: number[];
  onChange?: (value: number[]) => void;
};

const isSelected = (students, values) => students.every((s) => values.includes(s.id));

const ClassroomTreeView: React.FC<ClassroomTreeViewProps> = ({ classroom, values, onChange }) => {
  const classes = useStyles();

  const students = classroom.students ?? [];
  const groups = classroom.groups ?? ([] as any);
  const studentsWithoutGroup = students.filter(negate(isInGroup(groups) as any));

  const defaultExpanded = [classroom.id, ...groups.map((group) => group.id)].map(String);

  const handleChange = (checked: boolean, studentIds: number[]) => {
    let array = checked
      ? [...new Set([...values, ...studentIds])]
      : values.filter((value) => !studentIds.includes(value));

    // do not allow user to deselect all
    if (!array.length) array = [...new Set([...studentIds])];

    onChange?.(array);
  };

  const renderGroupNode = (group: Group) => {
    if (!group.groupEnrollments?.length) return null;

    const groupStudents = group.groupEnrollments.flatMap((ge) => ge?.student ?? []);

    return (
      <CheckBoxTreeItem
        key={group.id}
        id={group.id}
        label={group.name}
        checked={isSelected(groupStudents, values)}
        onChange={(event) =>
          handleChange(
            event.currentTarget.checked,
            groupStudents.map((student) => student.id)
          )
        }
      >
        {groupStudents.map(renderStudentNode)}
      </CheckBoxTreeItem>
    );
  };

  const renderStudentNode = (student: Student) => (
    <CheckBoxTreeItem
      key={student.id}
      id={student.id}
      label={`${student.firstName} ${student.lastName}`}
      checked={values.some((item) => item === student.id)}
      onChange={(event) => handleChange(event.currentTarget.checked, [student.id])}
    />
  );

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpanded={defaultExpanded}
      defaultExpandIcon={<ChevronRightIcon />}
      disableSelection={true}
      classes={{
        root: classes.treeViewRoot,
      }}
    >
      <CheckBoxTreeItem
        id={classroom.id as number}
        label={classroom.name as string}
        checked={isSelected(students, values)}
        onChange={(event) =>
          handleChange(
            event.currentTarget.checked,
            classroom.students?.map((student) => student.id) ?? []
          )
        }
      >
        {groups.map(renderGroupNode)}
        {studentsWithoutGroup.map(renderStudentNode as any)}
      </CheckBoxTreeItem>
    </TreeView>
  );
};

const useStyles = makeStyles(() => ({
  treeViewRoot: {
    minWidth: remCalc(244),
    padding: `${remCalc(6)} ${remCalc(12)}`,
  },
}));

export default ClassroomTreeView;
